import dayjs from "dayjs";

/**
 * "Called" filter of the view board: deals whose lead had a call (any channel,
 * any agent) in a day range. The URL is its only state:
 *   ?calledAtStart=today|yesterday|last7              → a range relative to today
 *   ?calledAtStart=YYYY-MM-DD&calledAtEnd=YYYY-MM-DD  → a custom range
 */
export const CALLED_AT_START_PARAM = "calledAtStart";
export const CALLED_AT_END_PARAM = "calledAtEnd";

export const CALLED_AT_PRESETS = ["today", "yesterday", "last7"] as const;
export type TCalledAtPreset = (typeof CALLED_AT_PRESETS)[number];

export type TCalledAt = { preset: TCalledAtPreset } | { start: string; end: string };

/** The day bounds GET /pipeline-view/{id}/deal reads, in the user's timezone. */
export type TCalledAtRange = { calledAtStart: string; calledAtEnd: string };

const DAY_FORMAT = "YYYY-MM-DD";

const PRESET_LABELS: Record<TCalledAtPreset, string> = {
  today: "Called today",
  yesterday: "Called yesterday",
  last7: "Called last 7 days",
};

export const PRESET_OPTIONS: { preset: TCalledAtPreset; label: string }[] = [
  { preset: "today", label: "Today" },
  { preset: "yesterday", label: "Yesterday" },
  { preset: "last7", label: "Last 7 days" },
];

const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).format(DAY_FORMAT) === value;

const isPreset = (value: string | null): value is TCalledAtPreset =>
  CALLED_AT_PRESETS.includes(value as TCalledAtPreset);

/** Anything else in the URL reads as "off". */
export const parseCalledAt = (params: URLSearchParams): TCalledAt | undefined => {
  const start = params.get(CALLED_AT_START_PARAM);
  const end = params.get(CALLED_AT_END_PARAM);
  if (isPreset(start)) return { preset: start };
  if (isDay(start) && isDay(end) && start <= end) return { start, end };
  return undefined;
};

/** Writes (or removes, when undefined) the filter, leaving the other params alone. */
export const writeCalledAt = (params: URLSearchParams, calledAt?: TCalledAt): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(CALLED_AT_START_PARAM);
  next.delete(CALLED_AT_END_PARAM);
  if (!calledAt) return next;
  if ("preset" in calledAt) {
    next.set(CALLED_AT_START_PARAM, calledAt.preset);
  } else {
    next.set(CALLED_AT_START_PARAM, calledAt.start);
    next.set(CALLED_AT_END_PARAM, calledAt.end);
  }
  return next;
};

export const isSameCalledAt = (a?: TCalledAt, b?: TCalledAt) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Resolved on every request, so a board left open past midnight follows the new day. */
export const resolveCalledAt = (calledAt: TCalledAt): TCalledAtRange => {
  if (!("preset" in calledAt)) return { calledAtStart: calledAt.start, calledAtEnd: calledAt.end };
  const today = dayjs();
  const start = { today, yesterday: today.subtract(1, "day"), last7: today.subtract(6, "day") }[calledAt.preset];
  const end = calledAt.preset === "yesterday" ? start : today;
  return { calledAtStart: start.format(DAY_FORMAT), calledAtEnd: end.format(DAY_FORMAT) };
};

export const calledAtLabel = (calledAt?: TCalledAt): string => {
  if (!calledAt) return PRESET_LABELS.today;
  if ("preset" in calledAt) return PRESET_LABELS[calledAt.preset];
  const start = dayjs(calledAt.start);
  const end = dayjs(calledAt.end);
  if (calledAt.start === calledAt.end) return `Called ${start.format("MMM D")}`;
  const startFormat = start.year() === end.year() ? "MMM D" : "MMM D, YYYY";
  return `Called ${start.format(startFormat)} – ${end.format("MMM D, YYYY")}`;
};
